/*
 * Product page redesign — motion. Loaded by each section that uses it, so it
 * guards against running twice.
 *
 * When the bundle changes, observed from the outside so no theme code is
 * touched:
 *
 *   1. The price rolls to its new value digit by digit. The theme swaps the
 *      whole price row's HTML after fetching the new variant; a watcher on
 *      that row sees the old and new figures and plays an odometer over the
 *      new one while its real text sits underneath, invisible. The row is
 *      always the final price's width, so nothing around it moves.
 *
 * In the specs section:
 *
 *   2. The plain-English scales light up like charge LEDs the first time
 *      each row comes into view.
 *
 * In the owner stories:
 *
 *   3. The photos drift a few pixels inside their frames as the page scrolls.
 *
 * Reduced motion: the prices still update, but nothing animates, and the
 * scales are simply lit.
 */
(function () {
  if (window.pdpMotionLoaded) return;
  window.pdpMotionLoaded = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canAnimate = !reduce && typeof Element !== 'undefined' && !!Element.prototype.animate;

  /* -------------------------------------------------------------- odometer */

  var LIVE_PRICE =
    '.price--on-sale .price__sale .price-item--sale,' +
    '.price:not(.price--on-sale) .price__regular .price-item--regular';

  function amount(text) {
    var n = parseFloat(String(text).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? null : n;
  }

  // Same length and the non-digits ($ , .) in the same places.
  function sameShape(a, b) {
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
      if (/\d/.test(a[i]) !== /\d/.test(b[i])) return false;
      if (!/\d/.test(a[i]) && a[i] !== b[i]) return false;
    }
    return true;
  }

  function fadeIn(el, delay) {
    if (!el || !canAnimate) return;
    el.animate(
      [{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }],
      { duration: 280, delay: delay || 0, easing: 'ease-out', fill: 'backwards' }
    );
  }

  // The text node that holds the figure, and where the figure starts in it.
  function findText(el, text) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      var at = node.textContent.indexOf(text);
      if (at !== -1) return { node: node, at: at };
    }
    return null;
  }

  function roll(el, from, to) {
    var text = sameShape(from, to) && findText(el, to);
    if (!text) {
      fadeIn(el, 0);
      return;
    }

    var up = (amount(to) || 0) >= (amount(from) || 0);
    var previousColor = el.style.color;

    var overlay = document.createElement('span');
    overlay.className = 'pdp-odo';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.color = getComputedStyle(el).color;
    el.classList.add('pdp-odo-host');
    el.appendChild(overlay);

    // Every character is placed exactly where the real price draws it,
    // measured from the real text, so the last frame lands on it with no
    // jump. The font's own widths, kerning and letter-spacing are all in
    // these numbers.
    var origin = overlay.getBoundingClientRect();
    var range = document.createRange();
    var anims = [];
    var digits = to.replace(/\D/g, '').length;
    var digitIndex = 0;

    for (var i = 0; i < to.length; i++) {
      range.setStart(text.node, text.at + i);
      range.setEnd(text.node, text.at + i + 1);
      var r = range.getBoundingClientRect();

      var cell = document.createElement('span');
      cell.className = 'pdp-odo__cell';
      cell.style.left = (r.left - origin.left) + 'px';
      cell.style.top = (r.top - origin.top) + 'px';
      cell.style.width = r.width + 'px';
      cell.style.height = r.height + 'px';
      cell.style.lineHeight = r.height + 'px';
      overlay.appendChild(cell);

      var c = to[i];
      if (!/\d/.test(c)) {
        cell.textContent = c;
        continue;
      }
      digitIndex++;
      var a = parseInt(from[i], 10);
      var b = parseInt(c, 10);
      if (a === b) {
        cell.textContent = c;
        continue;
      }

      // Every digit the wheel passes on the way, in the direction of travel.
      var seq = [a];
      for (var d = a; d !== b; ) {
        d = up ? (d + 1) % 10 : (d + 9) % 10;
        seq.push(d);
      }
      if (!up) seq.reverse();

      cell.classList.add('pdp-odo__slot');
      var col = document.createElement('span');
      col.className = 'pdp-odo__col';
      for (var k = 0; k < seq.length; k++) {
        var line = document.createElement('span');
        line.textContent = seq[k];
        col.appendChild(line);
      }
      cell.appendChild(col);

      var travel = (seq.length - 1) * r.height;
      // The right-most digits move first, the way a mechanical counter turns.
      anims.push(col.animate(
        [
          { transform: 'translateY(' + (up ? 0 : -travel) + 'px)' },
          { transform: 'translateY(' + (up ? -travel : 0) + 'px)' }
        ],
        { duration: 700, delay: (digits - digitIndex) * 35, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'both' }
      ));
    }

    function cleanup() {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      el.classList.remove('pdp-odo-host');
    }

    // Hand back to the real text under a short fade. The glyphs are in the
    // same places, but the rolling ones were drawn on their own layer, and
    // the fade hides any sub-pixel difference in their edges.
    function done() {
      el.style.color = previousColor;
      overlay.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, fill: 'forwards' })
        .finished.then(cleanup, cleanup);
    }

    if (!anims.length) return cleanup();
    el.style.color = 'transparent';
    Promise.all(anims.map(function (x) { return x.finished; })).then(done, done);
  }

  /* ---------------------------------------------------------------- scales */

  var LEAD = 200; // the first dot pops before the rail sets off
  var STEP = 420; // the rail running from one dot to the next
  var ROW_GAP = 300; // at least this between one row starting and the next

  // The first dot pops, then the rail charges along to the next dot, easing
  // into it, and that dot pops as the rail arrives, and so on up to the
  // score. The score dot pops biggest.
  function lightScale(row, delay) {
    var gaps = row.querySelectorAll('.pdp-scale__step').length - 1;
    var lit = row.querySelectorAll('.pdp-scale__step--past, .pdp-scale__step--on');
    var fill = row.querySelector('.pdp-scale__fill');

    if (fill) {
      fill.classList.remove('is-off');
      if (gaps > 0 && lit.length > 1) {
        var frames = [];
        for (var k = 0; k < lit.length; k++) {
          frames.push({
            transform: 'scaleX(' + k / gaps + ')',
            offset: k / (lit.length - 1),
            easing: 'cubic-bezier(.45,0,.3,1)'
          });
        }
        fill.animate(frames, { duration: STEP * (lit.length - 1), delay: delay + LEAD, fill: 'backwards' });
      }
    }

    Array.prototype.forEach.call(lit, function (dot, i) {
      var top = i === lit.length - 1;
      setTimeout(function () {
        dot.classList.remove('is-off');
        dot.animate(
          [
            { transform: 'scale(0.3)', easing: 'cubic-bezier(.2,.9,.3,1)' },
            { transform: 'scale(' + (top ? 1.55 : 1.3) + ')', offset: 0.45, easing: 'cubic-bezier(.4,0,.2,1)' },
            { transform: 'none' }
          ],
          { duration: top ? 760 : 520 }
        );
      }, delay + (i ? LEAD + i * STEP : 0));
    });
  }

  // Each row waits, lights off, until it is properly in view, then plays
  // once. Rows that arrive together still go one after another.
  function armScales(block) {
    if (block.dataset.pdpMotion) return;
    block.dataset.pdpMotion = '1';
    if (!canAnimate || !('IntersectionObserver' in window)) return;

    var rows = block.querySelectorAll('.pdp-scale');
    Array.prototype.forEach.call(rows, function (row) {
      row.querySelectorAll('.pdp-scale__step--past, .pdp-scale__step--on, .pdp-scale__fill').forEach(function (el) {
        el.classList.add('is-off');
      });
    });

    var nextStart = 0;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        var now = performance.now();
        var start = Math.max(now, nextStart);
        nextStart = start + ROW_GAP;
        lightScale(entry.target, start - now);
      });
    }, { threshold: 0.6, rootMargin: '0px 0px -10% 0px' });

    Array.prototype.forEach.call(rows, function (row) { io.observe(row); });
  }

  /* --------------------------------------------------------------- stories */

  // The owner photos drift a few pixels inside their frames as the page
  // scrolls: enough to feel alive, not enough to notice. Only photos near the
  // screen are touched.
  var DRIFT = 10; // px either way; the photo is scaled 1.08, so an edge never shows

  function driftStories(grid) {
    if (grid.dataset.pdpMotion) return;
    grid.dataset.pdpMotion = '1';
    if (!canAnimate || !('IntersectionObserver' in window)) return;

    var photos = grid.querySelectorAll('.pdp-story__img');
    if (!photos.length) return;
    var near = new Set();
    var ticking = false;

    function update() {
      ticking = false;
      var mid = window.innerHeight / 2;
      near.forEach(function (img) {
        var r = img.parentNode.getBoundingClientRect();
        // -1 with the frame's centre a screen below the middle, 1 a screen above
        var p = Math.max(-1, Math.min(1, (mid - (r.top + r.height / 2)) / (mid + r.height / 2)));
        img.style.transform = 'translateY(' + (p * DRIFT).toFixed(2) + 'px) scale(1.08)';
      });
    }
    function request() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) near.add(e.target);
        else near.delete(e.target);
      });
      request();
    }, { rootMargin: '15% 0px' });

    Array.prototype.forEach.call(photos, function (img) {
      img.classList.add('is-drifting');
      io.observe(img);
    });
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
  }

  /* ------------------------------------------------------------------ wire */

  function init(root) {
    if (root.dataset.pdpMotion) return;
    root.dataset.pdpMotion = '1';

    // The price rolls when the theme swaps the row in.
    var row = root.querySelector('.pdp-buy__price-row');
    if (!row) return;
    var live = row.querySelector(LIVE_PRICE);
    var last = live ? live.textContent.trim() : '';

    new MutationObserver(function () {
      var el = row.querySelector(LIVE_PRICE);
      var next = el ? el.textContent.trim() : '';
      if (el && last && next && next !== last && canAnimate) {
        roll(el, last, next);
        // The strike-through lands with the price, the saving a beat after.
        fadeIn(row.querySelector('dd.price__compare'), 120);
        fadeIn(row.querySelector('.pdp-buy__save'), 420);
      }
      if (next) last = next;
    }).observe(row, { childList: true });
  }

  function boot() {
    document.querySelectorAll('.pdp-hero[data-section-id]').forEach(init);
    document.querySelectorAll('.pdp-scales').forEach(armScales);
    document.querySelectorAll('.pdp-masonry').forEach(driftStories);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: re-run when the section is re-rendered.
  document.addEventListener('shopify:section:load', boot);
})();
